import { formatCurrency } from '@/shared/lib/format';
import type { BookingResponse } from '@/shared/api';
import { parseJsonArray } from './helpers';
import { SectionCard } from './SectionCard';

interface ChannexService {
  name?: string;
  title?: string;
  total_price?: number | string;
  totalPrice?: number | string;
  price?: number | string;
}

function serviceAmount(s: ChannexService, currency: string): string {
  const raw = s.total_price ?? s.totalPrice ?? s.price ?? null;
  if (raw == null) return '—';
  const num = typeof raw === 'number' ? raw : Number(raw);
  return Number.isNaN(num) ? '—' : formatCurrency(num, currency);
}

/**
 * Leistungen, die der Gast beim Kanal mitgebucht hat (Channex `services`). Sie standen bisher
 * in einer Karte, die nirgends eingebunden war – sichtbar war davon nichts.
 */
export function BookedServicesCard({
  booking,
}: {
  booking: Pick<BookingResponse, 'servicesJson' | 'currency'>;
}) {
  const services = parseJsonArray<ChannexService>(booking.servicesJson);
  // Ein unlesbarer Eintrag ist nicht „keine Leistungen“ – dann sagt die Karte das.
  const unreadable =
    services.length === 0 && !!booking.servicesJson?.trim() && booking.servicesJson.trim() !== '[]';
  if (services.length === 0 && !unreadable) return null;
  if (unreadable) {
    return (
      <SectionCard icon="Receipt" title="Mitgebuchte Leistungen">
        <p className="text-ink-soft text-sm">
          Die mitgebuchten Leistungen vom Kanal ließen sich nicht lesen.
        </p>
      </SectionCard>
    );
  }
  return (
    <SectionCard icon="Receipt" title="Mitgebuchte Leistungen">
      <ul className="border-hairline rounded-control divide-hairline divide-y border">
        {services.map((s, i) => (
          <li
            key={`${s.title ?? s.name ?? 'Leistung'}-${i}`}
            className="flex justify-between gap-3 px-3 py-2 text-sm"
          >
            <span>{s.title ?? s.name ?? 'Leistung'}</span>
            <span className="text-ink-soft font-mono">{serviceAmount(s, booking.currency)}</span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}
